/**
 * Sync engine facade — preserves the existing public API while delegating
 * all logic to the SyncService class.
 *
 * This file exists so that every consumer (useSync, auth store, SyncIndicator,
 * import, register) can continue importing from `lib/sync` without changes.
 *
 * All sync logic now lives in `lib/services/syncService.ts` with dependency
 * injection for testability.
 */

import type { SyncResult, SyncState } from "../types";
import { getSyncService } from "./services";
import { execute, notifyChanges, queryOne } from "./sqlite";
import { ENGINE_UNSENT_SQL } from "./services/syncStorage";

// ── Re-export SyncResult type for consumers that import it from here ────

export type { SyncResult } from "../types";

// ── Sync state ──────────────────────────────────────────────────────────

export function getSyncState(): SyncState {
  return getSyncService().getSyncState();
}

// ── Push / Pull / Full sync ─────────────────────────────────────────────

export async function syncToServer(): Promise<SyncResult> {
  return getSyncService().pushToServer();
}

export async function syncFromServer(): Promise<{ hasChanges: boolean }> {
  return getSyncService().pullFromServer();
}

export function fullSync(): Promise<SyncResult> {
  return getSyncService().fullSync();
}

// ── Conflict resolution ─────────────────────────────────────────────────

export async function resolveConflict(
  noteId: string,
  resolution: "local" | "server",
): Promise<void> {
  return getSyncService().resolveConflict(noteId, resolution);
}

// ── Helper accessors ────────────────────────────────────────────────────
// These remain synchronous for backward compatibility with SyncIndicator
// and Sidebar, which call them directly in render. The underlying SQLite
// operations are synchronous (sql.js is WASM, not async).

const MAX_RETRIES = 3;

export function getPendingCount(): number {
  // Queued ops, plus v2 notes whose text the server has not confirmed (none in legacy mode).
  const row = queryOne<{ cnt: number }>(
    `SELECT (SELECT COUNT(*) FROM sync_queue) + (SELECT COUNT(*) FROM notes WHERE ${ENGINE_UNSENT_SQL}) as cnt`,
  );
  return row?.cnt ?? 0;
}

export function getFailedCount(): number {
  const row = queryOne<{ cnt: number }>(
    "SELECT COUNT(*) as cnt FROM sync_queue WHERE retries >= ?",
    [MAX_RETRIES],
  );
  return row?.cnt ?? 0;
}

export function retryFailedOps(): void {
  execute("UPDATE sync_queue SET retries = 0 WHERE retries >= ?", [MAX_RETRIES]);
}

export function clearFailedOps(): void {
  execute("DELETE FROM sync_queue WHERE retries >= ?", [MAX_RETRIES]);
  notifyChanges(["sync_queue"]);
}

export function getConflictCount(): number {
  const row = queryOne<{ cnt: number }>(
    "SELECT COUNT(*) as cnt FROM notes WHERE sync_status = 'conflict'",
  );
  return row?.cnt ?? 0;
}
